import {useState, useEffect} from 'react';
import {Box, Card, Container, Stack, Typography} from '@mui/material';
import Page from '../components/Page';
import {
  AccountBalanceOutlined,
  AccountBalanceWalletOutlined,
  CampaignOutlined,
  ConfirmationNumberOutlined,
  DiamondOutlined,
  GroupsOutlined,
  InsightsOutlined,
  PaidOutlined,
  PersonAddAlt1Outlined,
  ReceiptLongOutlined
} from '@mui/icons-material';
import {asyncRequest} from 'src/services/network';
import {useNavigate} from 'react-router';

// ----------------------------------------------------------------------

const fmtNum = (n) => {
  const v = Number(n) || 0;
  return v.toLocaleString('zh-CN', {maximumFractionDigits: 2});
};

const fmtMoney = (n) => {
  const v = Number(n) || 0;
  return `¥ ${v.toLocaleString('zh-CN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
};

const fmtPrice = (n) => {
  const v = Number(n) || 0;
  return v.toFixed(6).replace(/\.?0+$/, '');
};

// ----------------------------------------------------------------------

function StatCard({title, value, icon, color = 'primary', sub, onClick}) {
  return (
    <Card
      onClick={onClick}
      sx={{
        p: 2.5,
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        boxShadow: 1,
        transition: 'box-shadow 0.2s, transform 0.2s',
        '&:hover': onClick ? {boxShadow: 4, transform: 'translateY(-2px)'} : {}
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <Box
          sx={{
            width: 46,
            height: 46,
            flexShrink: 0,
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: `${color}.lighter`,
            color: `${color}.darker`
          }}
        >
          {icon}
        </Box>
        <Box sx={{flexGrow: 1, minWidth: 0}}>
          <Typography variant="body2" color="text.secondary" noWrap>
            {title}
          </Typography>
          <Typography variant="h5" sx={{fontWeight: 700, lineHeight: 1.2}} noWrap>
            {value}
          </Typography>
          {sub && (
            <Typography variant="caption" color="text.secondary" noWrap title={sub}>
              {sub}
            </Typography>
          )}
        </Box>
      </Stack>
    </Card>
  );
}

function SectionTitle({children}) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{mb: 1.5, mt: 3}}>
      <Box sx={{width: 4, height: 16, borderRadius: 1, bgcolor: 'primary.main'}}/>
      <Typography variant="h6">{children}</Typography>
    </Stack>
  );
}

// ----------------------------------------------------------------------

export default function DashboardApp() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [hasHome, setHasHome] = useState(true);

  useEffect(() => {
    getStats();
    try {
      const admin = JSON.parse(localStorage.getItem('admin-userInfo') || '{}');
      setHasHome(!!admin.hasHomePage);
    } catch (e) {
      setHasHome(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function getStats() {
    const {data} = await asyncRequest({
      method: 'get',
      path: '/stats'
    });
    setStats(data);
  }

  const sections = [
    {
      title: '资金池',
      cards: [
        {title: '资金池', icon: <AccountBalanceOutlined/>, color: 'error', value: fmtMoney(stats.pinTuanCvFundPool), sub: `贡献值 ${fmtNum(stats.totalPinTuanCV)} · 单价 ${fmtPrice(stats.pinTuanCvPrice)}`, to: '/finance/point/list'}
      ]
    },
    {
      title: '今日概况',
      cards: [
        {title: '今日新增用户', icon: <PersonAddAlt1Outlined/>, color: 'primary', value: fmtNum(stats.users), to: '/user/list'},
        {title: '今日支付笔数', icon: <ReceiptLongOutlined/>, color: 'success', value: fmtNum(stats.payedOrders), to: '/finance/transaction/list'},
        {title: '今日营业额', icon: <PaidOutlined/>, color: 'warning', value: fmtMoney(stats.totalPayed), to: '/finance/transaction/list'}
      ]
    },
    {
      title: '平台资产',
      cards: [
        {title: '平台剩余权益金', icon: <AccountBalanceWalletOutlined/>, color: 'info', value: fmtMoney(stats.totalBalanceV2), to: '/finance/xfq/list'},
        {title: '平台剩余兑换券', icon: <ConfirmationNumberOutlined/>, color: 'secondary', value: fmtMoney(stats.totalDhqV2), to: '/finance/dhq/list'}
      ]
    },
    {
      title: '全网数据',
      cards: [
        {title: '全网权益值', icon: <InsightsOutlined/>, color: 'primary', value: fmtMoney(stats.totalEquityValue), to: '/finance/equity-value/list'},
        {title: '免费权益积分', icon: <CampaignOutlined/>, color: 'warning', value: fmtNum(stats.totalAdPoints), to: '/finance/free-benefit/list'},
        {title: '全网银豆', icon: <DiamondOutlined/>, color: 'info', value: fmtNum(stats.totalSilverBean), to: '/jingxi/dragon-bead-log'},
        {title: '全网用户数量', icon: <GroupsOutlined/>, color: 'success', value: fmtNum(stats.totalUsers), to: '/user/list'}
      ]
    }
  ];

  return (
    <Page title="控制台 | 首页">
      <Container maxWidth="xl">
        <Box sx={{pb: 5}}>
          <Typography variant="h4">欢迎回来</Typography>
        </Box>

        {hasHome ? (
          <Box>
            {sections.map((section) => (
              <Box key={section.title}>
                <SectionTitle>{section.title}</SectionTitle>
                <Box
                  sx={{
                    display: 'grid',
                    gap: 2,
                    gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)'}
                  }}
                >
                  {section.cards.map((card) => (
                    <StatCard
                      key={card.title}
                      title={card.title}
                      value={card.value}
                      icon={card.icon}
                      color={card.color}
                      sub={card.sub}
                      onClick={() => navigate(card.to)}
                    />
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={{height: '500px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'}}>
            <img style={{width: '200px', height: '200px'}} src={`${process.env.PUBLIC_URL}/static/logo.svg`} alt="logo"/>
            <Typography variant="h5" sx={{mt: 3, fontWeight: 600}}>鲸世优选</Typography>
          </Box>
        )}
      </Container>
    </Page>
  );
}
